/**
 * Temporary instrumentation: what the canvas WOULD send, at the four points where a backend
 * call belongs.
 *
 * ## Why this exists
 *
 * The canvas has no backend calls at all today — it reads `fixtures/questionnaires.ts` and
 * keeps positions in localStorage. So before wiring the API there is nothing to inspect, and
 * the payloads are the part most worth looking at first: the document is ~30 KB of generated
 * JSON, and whether it is RIGHT is not something anyone can tell from the model file.
 *
 * Each call prints the operation, the URI and the real payload, so the four requests can be
 * read in the console against a canvas you just edited.
 *
 * ## This is scaffolding, not a logging layer
 *
 * It writes to `console` unconditionally and parks the last payload on `window`, which is
 * exactly what a real client must not do — a 30 KB document on every autosave would make the
 * console unusable and retain every version in memory. It goes away when the operations are
 * actually called; see the four `cpaSaveLog` call sites in `CanvasPage`.
 */
import { type DecisionTreeDocument, documentSha256, serializeDocument } from './wire';

/** The four points where a backend call belongs. */
export type SavePoint =
  /** Page open: GetDecisionTreeDraft, then CreateDecisionTreeDraft when there is none. */
  | 'open'
  /** Debounce / interval / edit-count trigger: PutDecisionTreeDraft. */
  | 'autosave'
  /** The Save button: CommitDecisionTreeVersion. */
  | 'commit'
  /** Leaving edit mode: DeleteDecisionTreeDraft. */
  | 'discard';

const OPERATION: Record<SavePoint, { name: string; http: string }> = {
  open: { name: 'GetDecisionTreeDraft → CreateDecisionTreeDraft', http: 'GET/POST /decision-trees/{treeId}/draft' },
  autosave: { name: 'PutDecisionTreeDraft', http: 'PUT /decision-trees/{treeId}/draft' },
  commit: { name: 'CommitDecisionTreeVersion', http: 'POST /decision-trees/{treeId}/versions' },
  discard: { name: 'DeleteDecisionTreeDraft', http: 'DELETE /decision-trees/{treeId}/draft' },
};

const kb = (s: string) => `${(s.length / 1024).toFixed(1)} KB`;

export interface SaveLogInput {
  point: SavePoint;
  treeId: string;
  document?: DecisionTreeDocument;
  /** Expressions `toDocument` could not structure. Printed loudly: see below. */
  unconvertible?: { ownerId: string; kind: string; index: number; expression: string }[];
  /** Why the autosave fired, so the trigger logic can be checked against what it claims. */
  reason?: string;
  extra?: Record<string, unknown>;
}

/**
 * Print one would-be request.
 *
 * Async only because the commit's `documentSha256` is — `crypto.subtle` has no sync form.
 */
export async function cpaSaveLog(input: SaveLogInput): Promise<void> {
  const { point, treeId, document: doc, unconvertible, reason, extra } = input;
  const op = OPERATION[point];
  const json = doc ? serializeDocument(doc) : undefined;

  /* eslint-disable no-console */
  console.groupCollapsed(
    `%c[CPA ${point}]%c ${op.name}${reason ? ` · ${reason}` : ''}${json ? ` · ${kb(json)}` : ''}`,
    'background:#1c5bbd;color:#fff;padding:1px 6px;border-radius:3px;font-weight:600',
    'color:#5f6b7a',
  );
  console.log('HTTP  ', op.http.replace('{treeId}', treeId));

  if (doc && json) {
    console.log('document (expand me)', doc);
    console.log('counts ', {
      questions: doc.questions.length,
      transitions: doc.transitions.length,
      unconditionalTransitions: doc.transitions.filter((t) => !t.condition).length,
      eligibilityRules: doc.questions.reduce((n, q) => n + (q.eligibility?.length ?? 0), 0),
      positions: Object.keys(doc.layout.positions).length,
      bytes: json.length,
    });

    // The commit is the one that carries a hash, so only it pays for computing one.
    if (point === 'commit') {
      console.log('documentSha256', await documentSha256(json));
    }

    // Loud, because this is the one thing in the payload that is WRONG rather than merely
    // worth reading: 16 of the live tree's 30 eligibility rules cannot be structured yet, and
    // a save that shipped this document would lose them.
    if (unconvertible?.length) {
      console.warn(
        `${unconvertible.length} 条表达式无法结构化 —— 这些规则会从 document 里消失，` +
          `需要 document schema 补上 sum / fallback / answered / granted 四种 term 形状`,
      );
      console.table(unconvertible);
    }

    console.log('raw JSON (copy me)', json);
  }

  if (extra) console.log('other fields', extra);
  console.groupEnd();

  // Parked so it can be poked at after the group is collapsed, e.g.
  // `copy(window.__cpaLastSave.json)`.
  (window as unknown as { __cpaLastSave?: unknown }).__cpaLastSave = {
    point,
    treeId,
    document: doc,
    json,
    unconvertible,
    at: new Date().toISOString(),
  };
  /* eslint-enable no-console */
}
